/**
 * Typed wrappers for the citizen Paystack card-payment endpoints on
 * service-payment.
 *
 * Endpoints used:
 *   POST {paymentBase}/v1/card-payments/initialize  — open Paystack txn.
 *   POST {paymentBase}/v1/card-payments/verify      — confirm + persist result.
 *   GET  {paymentBase}/v1/card-payments/me          — paginated list for me.
 *
 * RBAC: every call requires the citizen JWT to carry the matching `*-own`
 * permission. These are seeded to the `customer` role by
 * `20260509_card_payment_perms.sql`.
 */
import { requestJson, type RequestJsonOptions } from '../api-client';
import { getCustomerToken } from '../auth/tokenStore';
import { env } from '../env';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface InitializeCardPaymentRequest {
  readonly amountMinor: number;
  readonly currency: 'NGN';
  readonly customerEmail: string;
  readonly parcelBookingDraftId?: string;
}

export interface InitializeCardPaymentResponse {
  readonly id: string;
  readonly reference: string;
  readonly authorizationUrl: string;
  readonly accessCode: string;
}

export interface VerifyCardPaymentResponse {
  readonly id: string;
  readonly reference: string;
  readonly status: 'SUCCESS' | 'FAILED' | 'PENDING';
  readonly amountMinor: number;
  readonly currency: string;
  readonly paidAt: string | null;
}

export interface CardPaymentSummary {
  readonly id: string;
  readonly reference: string;
  readonly customerId: string;
  readonly amountMinor: number;
  readonly currency: string;
  readonly status: string;
  readonly parcelBookingDraftId: string | null;
  readonly paidAt: string | null;
  readonly createdAt: string;
}

export interface ListCardPaymentsResponse {
  readonly items: readonly CardPaymentSummary[];
  // eslint-disable-next-line upmp/no-number-money -- row count, not money
  readonly total: number;
  readonly page: number;
  readonly pageSize: number;
}

// ---------------------------------------------------------------------------
// API calls
// ---------------------------------------------------------------------------

/** Open a Paystack card transaction. Returns the authorization URL + access
 *  code so the frontend can either jump to verify (test mode) or open the
 *  Paystack inline modal (live mode). */
export async function initializeCardPayment(
  req: InitializeCardPaymentRequest,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<InitializeCardPaymentResponse> {
  return requestJson<InitializeCardPaymentResponse>({
    method: 'POST',
    baseUrl: env.servicePaymentBaseUrl,
    path: '/v1/card-payments/initialize',
    body: req,
    bearerToken: getCustomerToken() ?? undefined,
    ...options,
  });
}

/** Verify a Paystack card transaction. Idempotent; safe to retry. */
export async function verifyCardPayment(
  reference: string,
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<VerifyCardPaymentResponse> {
  return requestJson<VerifyCardPaymentResponse>({
    method: 'POST',
    baseUrl: env.servicePaymentBaseUrl,
    path: '/v1/card-payments/verify',
    body: { reference },
    bearerToken: getCustomerToken() ?? undefined,
    ...options,
  });
}

/** List my card payments (paginated). */
export async function listMyCardPayments(
  query: { status?: 'PENDING' | 'SUCCESS' | 'FAILED'; page?: number; pageSize?: number } = {},
  options: Pick<RequestJsonOptions, 'fetchImpl' | 'signal'> = {},
): Promise<ListCardPaymentsResponse> {
  const qs = new URLSearchParams();
  if (query.status) qs.set('status', query.status);
  if (query.page) qs.set('page', String(query.page));
  if (query.pageSize) qs.set('pageSize', String(query.pageSize));
  const suffix = qs.toString().length > 0 ? `?${qs.toString()}` : '';
  return requestJson<ListCardPaymentsResponse>({
    method: 'GET',
    baseUrl: env.servicePaymentBaseUrl,
    path: `/v1/card-payments/me${suffix}`,
    bearerToken: getCustomerToken() ?? undefined,
    ...options,
  });
}

// ---------------------------------------------------------------------------
// Paystack inline modal loader (live-mode only)
// ---------------------------------------------------------------------------

const PAYSTACK_INLINE_SCRIPT_URL = 'https://js.paystack.co/v2/inline.js';
let paystackScriptPromise: Promise<void> | null = null;

interface PaystackPopHandle {
  newTransaction(opts: {
    accessCode: string;
    onSuccess?: (txn: { reference: string; status: string }) => void;
    onCancel?: () => void;
    onError?: (err: unknown) => void;
  }): void;
}

interface WindowWithPaystack extends Window {
  PaystackPop?: { setup?: () => PaystackPopHandle };
}

/** Lazily load the Paystack inline JS bundle (idempotent). Returns the global
 *  `PaystackPop` handle the caller can use to open the modal. Returns null if
 *  the load fails — caller should fall back to the authorization URL. */
export async function loadPaystackInline(): Promise<PaystackPopHandle | null> {
  if (typeof window === 'undefined') return null;
  const win = window as WindowWithPaystack;
  if (win.PaystackPop?.setup) return win.PaystackPop.setup();

  if (!paystackScriptPromise) {
    paystackScriptPromise = new Promise<void>((resolve, reject) => {
      const script = document.createElement('script');
      script.src = PAYSTACK_INLINE_SCRIPT_URL;
      script.async = true;
      script.onload = (): void => resolve();
      script.onerror = (): void => reject(new Error('Failed to load Paystack inline.js'));
      document.head.appendChild(script);
    });
  }

  try {
    await paystackScriptPromise;
    return win.PaystackPop?.setup ? win.PaystackPop.setup() : null;
  } catch {
    paystackScriptPromise = null;
    return null;
  }
}

/** True when the Paystack initialize response is the deterministic test-mode
 *  stub. The frontend skips the inline modal in this case and verifies
 *  directly so e2e + dev runs are hermetic. */
export function isTestModeAuthorizationUrl(url: string): boolean {
  return url.startsWith('https://test.paystack.local/');
}
